import type { ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ThemedPage } from '@/types'

import { Seo } from '@/components/generals/seo'
import FadeIn from '@/components/generals/fade-in'
import PageHero from '@/components/generals/page-hero'
import MenuBoard from '@/components/landing/menu/menu-board'
import { DEFAULT_MENU_TAB, MENU } from '@/constants/menu'

interface MenuPageProps {
    tab: string
}

const MenuPage: ThemedPage<MenuPageProps> = ({ tab }): ReactNode => {
    const category = MENU.find(item => item.slug === tab) ?? MENU[0]
    const isDefault = category.slug === DEFAULT_MENU_TAB

    return (
        <>
            <Seo
                title={isDefault ? 'Menu' : `${category.label} menu`}
                description={`${category.label}, ${category.subtitle.toLowerCase()}. ${category.intro} Halal kitchen, no alcohol, no pork.`}
            />

            <PageHero chapter='II' title={["Menu"]}>
                <FadeIn trigger='mount' delay={0.8} className='mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-10'>
                    <p className='text-[0.95rem] text-ivory/70'>
                        Halal kitchen. No alcohol. No pork.
                    </p>

                    <p className='caps flex items-center gap-3 text-ivory/55'>
                        <span aria-hidden='true' className='size-1.5 rounded-full bg-brass' />
                        Vegetarian
                    </p>
                </FadeIn>
            </PageHero>

            <MenuBoard tab={category.slug} />
        </>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: MENU.map(category => ({
            params: { tab: category.slug === DEFAULT_MENU_TAB ? [] : [category.slug] }
        })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<MenuPageProps> = async ({ params }) => {
    const value = params?.tab
    const segments = Array.isArray(value) ? value : []
    const slug = segments[0] ?? DEFAULT_MENU_TAB

    if (segments.length > 1 || !MENU.some(category => category.slug === slug)) {
        return { notFound: true }
    }

    return { props: { tab: slug } }
}

export default MenuPage
